import type { ProcedureCategory, ProcedureCode, ProcedureScope } from "@dentacore/shared-types";

import { RowMenu } from "../ui/popovers";
import { categoryColour } from "./categories-dialog";
import { formatMinor, plural, spanDays } from "./procedure-form";
import styles from "./procedures.module.css";

/**
 * One procedure, opened beside the list (TR3) — everything the row used to
 * carry and every button it used to have, so the row itself can be a name and a
 * price (TR2).
 *
 * **The visits are drawn as a line** (STORY-153, moved here by TR3): number,
 * name, *First visit* or *N days later*, minutes and price, for everyone who
 * reads the list — Anthony: *"Find a way to show both visits."*
 *
 * **Only an admin is drawn anything to press** (PL2, TR9). Everybody else reads
 * the same panel with *Done on* as words. The server refuses a non-admin's write
 * regardless; `canEdit` only spares them a button that would 403.
 *
 * **Its materials, their cost and the profit sit under the visits** (STORY-175b),
 * handed in whole as `materials` — the page owns that tray's reads and writes.
 */
export function ProcedurePanel({
  code,
  currency,
  canEdit,
  busy,
  onClose,
  onEdit,
  onDuplicate,
  onRetire,
  onRestore,
  onScope,
  onMissingTooth,
  materials,
  category,
  locations,
}: {
  /** The switch between this procedure's copies at each location shown. */
  locations?: React.ReactNode;
  code: ProcedureCode;
  currency: string;
  canEdit: boolean;
  busy: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onRetire: () => void;
  onRestore: () => void;
  onScope: (scope: ProcedureScope) => void;
  /** *Done where a tooth is missing* (PP-D4), saved the moment it changes. */
  onMissingTooth: (value: boolean) => void;
  /** The *Materials* section (MC-D1). */
  materials: React.ReactNode;
  /** Its category (PG-D4); null only while the list is still being read. */
  category: ProcedureCategory | null;
}): React.JSX.Element {
  const many = code.visits.length > 0;
  const where = code.scope === "WHOLE_MOUTH" ? "the whole mouth" : "one tooth";
  const span = spanDays(code);

  const note = !code.isActive
    ? "Retired, so it can no longer be booked."
    : many
      ? // A course with no gaps set yet is still a course; "over 0 days" is not.
        span > 0
        ? `${plural(code.visits.length, "visit")} over ${plural(span, "day")}`
        : plural(code.visits.length, "visit")
      : `One visit · ${String(code.defaultDurationMinutes)} min`;

  return (
    <aside
      className={styles.panel}
      aria-label={code.label}
      data-testid="proc-panel"
      data-procedure={code.id}
    >
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle} data-testid="proc-panel-title">
          {code.label}
        </h2>
        {canEdit && code.isActive && (
          <RowMenu
            label={`More for ${code.label}`}
            testId="proc-more"
            items={[
              {
                label: "Duplicate",
                disabled: busy,
                onSelect: onDuplicate,
                testId: "proc-duplicate",
              },
              /*
               * Retired, never deleted (C3): every invoice line and course that
               * names it keeps working, each carrying its own copied price.
               */
              {
                label: "Retire",
                danger: true,
                disabled: busy,
                onSelect: onRetire,
                testId: "proc-retire",
              },
            ]}
          />
        )}
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
          data-testid="proc-panel-close"
        >
          ×
        </button>
      </div>
      {locations !== undefined && <div className={styles.panelLocations}>{locations}</div>}
      <p className={styles.panelSub}>
        <span data-testid="proc-panel-code">{code.code}</span> · done on{" "}
        <span data-testid="proc-panel-scope-text">{where}</span>
        {category !== null && (
          <>
            {" · "}
            <span className={styles.categoryTag} data-testid="proc-panel-category">
              <span
                className={styles.categoryDot}
                style={{ "--swatch": categoryColour(category.colour) } as React.CSSProperties}
                aria-hidden="true"
              />
              {category.name}
            </span>
          </>
        )}
      </p>

      <div className={styles.panelBody}>
        <p className={styles.figure} data-testid="proc-panel-price">
          {formatMinor(code.defaultPriceCents, currency)}
        </p>
        <p className={styles.figureNote} data-testid="proc-panel-note">
          {note}
        </p>

        {many && (
          <ol className={styles.visits} data-testid="proc-panel-visits">
            {code.visits.map((visit, index) => (
              <li key={visit.id} className={styles.visit} data-testid="proc-panel-visit">
                <span className={styles.visitDot}>{index + 1}</span>
                <span className={styles.visitText}>
                  <span className={styles.visitWhat}>
                    {visit.title ?? `Visit ${String(index + 1)}`}
                  </span>
                  <span className={styles.visitWhen}>
                    {index === 0
                      ? "First visit"
                      : visit.gapDays === null
                        ? "No gap set"
                        : `${plural(visit.gapDays, "day")} later`}
                    {" · "}
                    {visit.durationMinutes} min
                  </span>
                </span>
                <span className={styles.visitPrice}>{formatMinor(visit.priceCents, currency)}</span>
              </li>
            ))}
          </ol>
        )}

        {materials}
      </div>

      {canEdit &&
        (code.isActive ? (
          <div className={styles.panelTools}>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={onEdit}
              data-testid="proc-edit"
            >
              Edit
            </button>
            {/*
              **Saved the moment it changes**, as the select on the row was. It
              will be changed after the fact: a clinic entering forty procedures
              leaves a whitening on the default, and a booking then asks which
              tooth to whiten (TP12).
            */}
            <label className={styles.doneOn}>
              <span>Done on</span>
              <select
                className={`${styles.input} ${styles.doneOnSelect}`}
                value={code.scope}
                disabled={busy}
                onChange={(event) => onScope(event.target.value as ProcedureScope)}
                data-testid="proc-panel-scope"
              >
                <option value="PER_TOOTH">One tooth</option>
                <option value="WHOLE_MOUTH">The whole mouth</option>
              </select>
            </label>
            {/*
              **An implant or a pontic goes where a tooth is missing** (PP-D4):
              a removed tooth refuses every procedure without this tick.
            */}
            {code.scope === "PER_TOOTH" && (
              <label className={styles.doneOn}>
                <input
                  type="checkbox"
                  checked={code.doneOnMissingTooth}
                  disabled={busy}
                  onChange={(event) => onMissingTooth(event.target.checked)}
                  data-testid="proc-panel-missing-tooth"
                />
                <span>Done where a tooth is missing</span>
              </label>
            )}
          </div>
        ) : (
          <div className={styles.panelTools}>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={onRestore}
              data-testid="proc-restore"
            >
              Bring back
            </button>
          </div>
        ))}
    </aside>
  );
}
